import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { oneSecondMilli } from '../constants-types/constants';
import { ApiService } from '../system-services/Api.service';
// Polling service used to call Apis after interval of time to see if certain condition is met
// More Info: https://levelup.gitconnected.com/polling-in-javascript-ab2d6378705a

/**
 * Polling Request
 */
export class Poll<TResultCheck> {
    /**
     * URL to call.  Ignored if requestFn is set.
     */
    url?: string;

    /**
     * Call to make on each iteration of the poll.
     */
    requestFn?: () => Promise<TResultCheck>;

    /**
     * Time between calls in seconds
     */
    intervalInSeconds: number;

    /**
     * Maximum amount of time this poll should run before quitting
     */
    maxDurationInSeconds: number;

    /**
     * Method to determine if the polling is considered successful
     */
    resultCheck: (result: TResultCheck) => boolean;
}

class ActivePoll {
    poll: Poll<any>;
    subscription?: Observable<any>;
    subject: Subject<any>;
    startTime: number;
}

@Injectable({ providedIn: 'root' })
export class PollingService {
    private activePolls: Map<string, ActivePoll> = new Map<
        string,
        ActivePoll
    >();

    constructor(private readonly apiService: ApiService) {}

    /**
     *
     * @param fn :  Function to be polled against
     * @param terminator :  Function that decides when to end the poll. Either an expected Success or failure
     * @param interval : Interval (in ms) between polling
     * @param maxAttempts : Number of attempts before timing out
     */
    async createPoll({
        fn,
        terminator,
        interval = oneSecondMilli,
        maxAttempts = 10
    }) {
        let attempts = 0;

        const executePoll = async (resolve, reject) => {
            const result = await fn();
            attempts++;

            if (terminator(result)) {
                return resolve(result);
            } else if (attempts === maxAttempts) {
                return reject(new Error('Exceeded max attempts'));
            } else {
                setTimeout(executePoll, interval, resolve, reject);
            }
        };

        return new Promise(executePoll);
    }

    /**
     *
     * @param key Poll Key
     * @returns An Observable for the requested poll, or undefined if one does not exist.
     */
    public getPollSubscription<TSubscriptionResult>(
        key: string
    ): Observable<TSubscriptionResult> {
        return this.activePolls.get(key)?.subscription;
    }

    /**
     *
     * @param key Poll Key
     * @param poll Poll configuration
     * @param reset If an existing poll with the same key is found, should the duration be reset to 0?
     * @returns An observable subscription triggered when the poll successfully completes (or errors when it times out)
     */
    public createPollSubscription<TResultCheck, TSubscriptionResult>(
        key: string,
        poll: Poll<TResultCheck>,
        reset = false
    ): Observable<TSubscriptionResult> {
        if (this.activePolls.has(key)) {
            const existingPoll = this.activePolls.get(key);

            if (reset) {
                existingPoll.startTime = Date.now();
            }

            return existingPoll.subscription;
        }

        if (poll.url) {
            poll.requestFn = () =>
                this.apiService.get<TResultCheck>(poll.url).then((x) => x[0]);
        }

        const activePoll: ActivePoll = {
            poll: poll,
            subject: new Subject<TSubscriptionResult>(),
            startTime: Date.now()
        };

        activePoll.subscription = activePoll.subject.asObservable();
        this.activePolls.set(key, activePoll);

        // Delay the initial execution by 1 ms to force it to the back of the line.
        // This ensures that callers get the subscription back before it has a chance to run the first check.
        //  (Prevents a race condition upon which the first check succeeds and the caller gets a subscription to something that is already completed)
        setTimeout(this.executePoll, 1, this, key);

        return activePoll.subscription;
    }

    private executePoll(service: PollingService, key: string) {
        const activePoll = service.activePolls.get(key);

        if (!activePoll) {
            return; // This shouldn't happen, but just in case.
        }

        const duration = (Date.now() - activePoll.startTime) / 1000;

        if (activePoll.poll.maxDurationInSeconds < duration) {
            activePoll.subject.error(
                `Poll ${key} exceeded max duration and has been cancelled`
            );
            service.activePolls.delete(key);

            return;
        }

        activePoll.poll
            .requestFn()
            .then((x) => {
                if (activePoll.poll.resultCheck(x)) {
                    activePoll.subject.next(x);
                    activePoll.subject.complete();
                    service.activePolls.delete(key);
                } else {
                    setTimeout(
                        service.executePoll,
                        activePoll.poll.intervalInSeconds * 1000,
                        service,
                        key
                    );
                }
            })
            .catch((error) => {
                activePoll.subject.error(error);
                service.activePolls.delete(key);
            });
    }
}
